import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import { useAuth, dashboardPathForRole } from "@/context/AuthContext";
import { IMAGES } from "@/constants/images";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { identifier, password });
      setUser(data);
      toast.success(`Selamat datang, ${data.name}`);
      const redirectTo = location.state?.from?.pathname || dashboardPathForRole(data.role);
      navigate(redirectTo, { replace: true });
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={IMAGES.heroKaaba} alt="Masjidil Haram" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#084C3A]/90 via-[#0F6B4F]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-2xl font-bold leading-snug">
            Mempersiapkan Perjalanan Ibadah dengan Ilmu, Bimbingan, dan Ketulusan.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-[#F5F9F7] px-6 py-16">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-[0_8px_40px_rgba(15,107,79,0.08)] sm:p-10" data-testid="login-card">
          <Link to="/" className="mb-6 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0F6B4F] to-[#1677B8] text-white font-heading font-bold">K</div>
            <span className="font-heading text-lg font-bold text-[#1F2937]">KBIHU Muhammadiyah Surabaya</span>
          </Link>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Masuk ke KBIHU Muhammadiyah Surabaya</h1>
          <p className="mt-1 text-sm text-[#64748B]">Silakan masuk untuk mengakses dashboard Anda.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="identifier">Email / Nomor HP</Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="nama@email.com atau 08xxxxxxxxxx"
                required
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 rounded-xl pr-10"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                  aria-label="Tampilkan password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[#1F2937]">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember-checkbox" />
                Ingat saya
              </label>
              <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-sm font-medium text-[#0F6B4F] hover:underline">
                Lupa Password?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="h-11 w-full rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white hover:opacity-90"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            Belum memiliki akun?{" "}
            <Link to="/register" data-testid="login-register-link" className="font-medium text-[#0F6B4F] hover:underline">
              Daftar Sekarang
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
